import { Button, DialogActions, DialogTitle } from '@mui/material'
import { useCallback, useMemo, useState } from 'react'

import { QueryParamNames } from '../../../models/QueryParamNames'
import { PartOverridePlaceholderTypeEnum, ServiceExpanded } from '../../../sdk'
import {
  generatePartsTechSearchPath,
  partsTechSearchParams
} from '../../../utils/generatePartsTechSearchPath/generatePartsTechSearchPath'
import {
  getSelectedMotorFluidsFromCannedJob,
  getSelectedMotorJobFromCannedJob,
  getSelectedPartLookup,
  ModifiedCannedJobExpanded
} from '../useLookupServiceDialog/transformSelectedJob'
import { SelectMultiFluids } from '../useSelectFluids/SelectMultiFluids'
import { SelectMultiLabor } from '../useSelectMotorLabortsx/SelectMultiLabor'

enum StepName {
  SELECT_LABOR = 'Select labor',
  SOURCE_PARTS_CONFIRM = 'Labor selection done. Do you want to source parts?',
  SELECT_FLUIDS = 'Source fluids',
  SELECT_LOOKUP = 'Source parts on partsTech'
}

export const LaborAndPartsStepper = ({
  services,
  cannedJob,
  submitCallback,
  cancelCallback
}: {
  services: ServiceExpanded[]
  cannedJob: ModifiedCannedJobExpanded[]
  submitCallback: () => void
  cancelCallback: () => void
}) => {
  const motorLabors = cannedJob
    .map((c) => getSelectedMotorJobFromCannedJob(c))
    .flat()

  const motorFluids = cannedJob
    .map((c) => getSelectedMotorFluidsFromCannedJob(c))
    .flat()

  const partLookups = cannedJob.map((c) => getSelectedPartLookup(c)).flat()

  const steps: StepName[] = useMemo(() => {
    const temp = []
    if (motorLabors.length > 0) {
      temp.push(StepName.SELECT_LABOR)
    }
    if (motorLabors.length > 0 && motorFluids.length + partLookups.length > 0) {
      temp.push(StepName.SOURCE_PARTS_CONFIRM)
    }
    if (motorFluids.length > 0) {
      temp.push(StepName.SELECT_FLUIDS)
    }
    if (partLookups.length > 0) {
      temp.push(StepName.SELECT_LOOKUP)
    }
    return temp
  }, [motorLabors.length, motorFluids.length, partLookups.length])

  const [index, setIndex] = useState(0)
  const stepSubmitCallback = useCallback(() => {
    if (index === steps.length - 1) {
      submitCallback()
    } else {
      setIndex(index + 1)
    }
  }, [index, steps.length, submitCallback])
  const onOpenPartsTech = useCallback(() => {
    const allPartsTechPlaceholders = services
      .flatMap((s) => s.partOverrides!)
      .filter(
        (p) =>
          p.placeholderType === PartOverridePlaceholderTypeEnum.PartsTechLookup
      )
    const arr: partsTechSearchParams[] = []
    allPartsTechPlaceholders.forEach((p) => {
      arr.push({
        [QueryParamNames.SERVICE_ID]: p.service!,
        [QueryParamNames.KEYWORD]: p.partName!,
        [QueryParamNames.PARTS_MATRIX]: p.markup || '',
        [QueryParamNames.PART_OVERRIDE]: p.uuid!,
        [QueryParamNames.PART_TYPE_ID]: p.partTaxonomy?.partType?.id
          ? String(p.partTaxonomy.partType.id)
          : ''
      })
    })
    const path = generatePartsTechSearchPath(arr)
    window.open(path)
    submitCallback()
  }, [services, submitCallback])
  const content = useMemo(() => {
    switch (steps[index]) {
      case StepName.SELECT_LABOR:
        return (
          <SelectMultiLabor
            services={services}
            motorLabors={motorLabors}
            submitCallback={stepSubmitCallback}
          />
        )
      case StepName.SOURCE_PARTS_CONFIRM:
        return (
          <>
            <DialogActions>
              <Button onClick={cancelCallback}>Skip</Button>
              <Button variant="contained" onClick={stepSubmitCallback}>
                Source Parts
              </Button>
            </DialogActions>
          </>
        )
      case StepName.SELECT_FLUIDS:
        return (
          <SelectMultiFluids
            motorFluids={motorFluids}
            services={services}
            submitCallback={stepSubmitCallback}
          />
        )
      case StepName.SELECT_LOOKUP:
        return (
          <>
            <DialogActions>
              <Button onClick={cancelCallback}>Skip</Button>
              <Button variant="contained" onClick={onOpenPartsTech}>
                Open PartsTech
              </Button>
            </DialogActions>
          </>
        )
      default:
        return <></>
    }
  }, [
    cancelCallback,
    index,
    motorFluids,
    motorLabors,
    onOpenPartsTech,
    services,
    stepSubmitCallback,
    steps
  ])
  return (
    <>
      <DialogTitle>{steps[index]}</DialogTitle>
      {content}
    </>
  )
}
